//@flow

export type CookieDetailsType = {
    is_native: *;
    device_os: *;
    os_version: *;
    is_tablet: *;
    app_version: *;
};

export type DeviceInfoDetailsType = {
    os: string,
    tablet: boolean,
    native: boolean,
    version: number,
    appVersion: null | string,
};

/**
 * Device info class
 */
class DeviceInfo {
    info: DeviceInfoDetailsType;
    isPristine: boolean;

    /**
     * Constructor
     * @constructor
     */
    constructor() {
        this.info = {
            os: 'ios',
            tablet: true,
            native: false,
            version: 0,
            appVersion: null,
        };
        this.isPristine = true;
    }

    /**
     * Choose value if condition is true
     * @param {any} condition Any expression
     * @param {Function} valueFunc Function that returns value
     * @param {any} defaultValue Default value
     * @returns {*} New or default value
     */
    setIfTrue(condition: boolean, valueFunc: () => *, defaultValue: *) {
        if (this.isPristine && condition) {
            this.isPristine = false;
        }
        return condition ? valueFunc() : defaultValue;
    }

    setup(data: CookieDetailsType) {
        this.info.os = this.setIfTrue(!!data.device_os, () => data.device_os.toLowerCase(), this.info.os);
        this.info.tablet = this.setIfTrue(typeof data.is_tablet === 'boolean',
            () => data.is_tablet, this.info.tablet);
        this.info.native = this.setIfTrue(typeof data.is_native === 'boolean',
            () => data.is_native, this.info.native);
        this.info.version = this.setIfTrue(typeof data.os_version === 'number',
            () => data.os_version, this.info.version);
        this.info.appVersion = this.setIfTrue(typeof data.app_version === 'string',
            () => data.app_version, this.info.appVersion);
    }

    /**
     * Returns os name
     *
     * @returns {string} os name
     *
     * @public
     */
    get osName(): string {
        return this.info.os;
    }

    /**
     * Checks if device is tablet
     *
     * @returns {boolean} is devide tablet
     *
     * @public
     */
    get isTablet(): boolean {
        return this.info.tablet === true;
    }


    /**
     * Checks if device is native
     *
     * @returns {boolean} is devide native
     *
     * @public
     */
    get isNative(): boolean {
        return this.info.native === true;
    }
    /**
     * Checks if device is desktop
     *
     * @returns {boolean} is device desktop
     *
     * @public
     */
    get isDesktop(): boolean {
        return this.info.os === 'desktop';
    }
    /**
     * Checks if device is ios & native
     *
     * @returns {boolean} is devie ios and native
     *
     * @public
     */
    get isIosNative(): boolean {
        return this.info.os === 'ios' && this.isNative;
    }
    /**
     * Checks os version
     *
     * @returns {number} os number
     *
     * @public
     */
    get osVersion(): number {
        return this.info.version;
    }

    /**
     * Checks native app version
     *
     * @returns {null | string} string with app version when valid (context of native app wrapper),
     * null when not valid (web application)
     *
     * @public
     */
    get appVersion(): null | string {
        return this.info.appVersion;
    }

    /**
     * Checks if device is ios
     *
     * @returns {boolean} is ios
     *
     */
    get isIos(): boolean {
        return this.info.os === 'ios';
    }
    /**
     * Checks if device is android
     *
     * @returns {boolean} is device android
     *
     * @public
     */
    get isAndroid(): boolean {
        return this.info.os === 'android';
    }

    /**
     * Checks if is running in mobile web
     *
     * @returns {boolean} is mobile
     *
     * @public
     */
    get isMobile(): boolean {
        return (!this.isDesktop && !this.isNative && !this.isTablet);
    }

    /**
     * Returns the device params in a stringified form os[-tablet]-native||-web
     * @returns {string} device string
     */
    toString() {
        return this.osName + (this.isTablet ? '-tablet' : '') + (this.isNative ? '-native' : '-web');
    }
}

export default DeviceInfo;
